/**
 * Revocation banner — UX signal for the cross-user access-change
 * gap left by surfaces 1-4 (#306 surface 5).
 *
 * Mounted once at app root. Listens for the
 * ``api:auth-revoked`` custom event dispatched by
 * ``api/client.ts``'s ``request()`` when:
 *   - The backend returns 401 or 403, AND
 *   - Neither auto-recovery function (dev-token / student-token)
 *     claimed the failure.
 *
 * That gap captures cases like:
 *   - Clerk-authed teacher whose org membership was revoked by
 *     an admin. Their Clerk session still appears valid client-
 *     side, but the backend's org-scoped permission checks
 *     reject every protected call until they reload (or until
 *     Clerk's session refreshes through a sign-in flow).
 *   - Student removed from class by a teacher with a generic 403
 *     return shape that doesn't match the student-token recovery
 *     pattern.
 *   - Any other server-side permission revocation between login
 *     and the next API call.
 *
 * Pre-#306 surface 5 these cases were silent — the user kept
 * clicking around and hitting the same errors until they happened
 * to reload. The banner is the explicit "reload to see the latest
 * state" signal.
 *
 * Visibility gating: shows ONLY when the user has some kind of
 * credential in localStorage (student token, dev teacher token,
 * or any Clerk-shaped localStorage key). Without that gate, a
 * route-level 401 on a truly-logged-out user (e.g. direct link to
 * a protected page) would surface the banner — confusing because
 * they're not "revoked", they're just not signed in. The login
 * flow's own error UI handles that case.
 *
 * Dismiss behavior: clicking ✕ hides the banner for the current
 * page lifecycle. A subsequent 401/403 re-shows it. Reloading the
 * page resets state naturally — typically the user reloads as
 * directed.
 */
import { useEffect, useState } from 'react'

import { useT } from '../i18n'
import {
  REVOCATION_EVENT_NAME,
  getStoredStudentToken,
  type RevocationEventDetail,
} from '../api/client'
import { getDevTeacherToken } from '../api/devToken'


/**
 * Cheapest signal that the user has SOME kind of session-shaped
 * credential. Used to gate the banner so a truly-logged-out 401
 * (e.g. SiteGate probe, direct-link to protected route) doesn't
 * trip a confusing "you've been revoked" message.
 *
 * Detects:
 *   - Student session token (localStorage)
 *   - Dev teacher token (localStorage, dev harness only)
 *   - Any Clerk-shaped localStorage key (production teacher /
 *     admin auth). Clerk's exact key shape isn't stable across
 *     SDK versions, so we glob — any key starting with
 *     ``__clerk`` is a Clerk-owned cache entry that exists only
 *     when a Clerk session has touched localStorage.
 */
function hasAnyCredential(): boolean {
  if (typeof window === 'undefined') return false
  try {
    if (getStoredStudentToken()) return true
    if (getDevTeacherToken()) return true
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('__clerk')) return true
    }
  } catch {
    // localStorage blocked / unavailable — defensively don't show.
  }
  return false
}


export default function RevocationBanner() {
  const t = useT()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const onRevoked = (e: Event) => {
      // Narrow the event shape. The custom-event payload carries
      // ``{ status, path }`` but the banner doesn't need either —
      // status is always 401 or 403 (filtered upstream), and the
      // path is for operator triage if we add logging later.
      const detail = (e as CustomEvent<RevocationEventDetail>).detail
      if (!detail) return
      // Gate visibility on the user actually having a credential.
      // See ``hasAnyCredential`` for the rationale.
      if (!hasAnyCredential()) return
      setVisible(true)
    }
    window.addEventListener(REVOCATION_EVENT_NAME, onRevoked)
    return () => window.removeEventListener(REVOCATION_EVENT_NAME, onRevoked)
  }, [])

  if (!visible) return null

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10000,
        maxWidth: 520,
        background: 'var(--surface, #fff)',
        border: '1px solid var(--warn)',
        borderRadius: 8,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
        padding: '14px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          {t('revocation.banner.title')}
        </div>
        <div style={{ fontSize: 14, lineHeight: 1.5, opacity: 0.85 }}>
          {t('revocation.banner.body')}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => setVisible(false)}
        >
          {t('revocation.banner.dismiss')}
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => {
            // Hard reload rather than ``router.refresh()`` — Clerk
            // session state, in-memory caches, and any other
            // module-scope state should all start fresh. This is
            // the "your access changed" reset button; full reload
            // is the right granularity.
            window.location.reload()
          }}
        >
          {t('revocation.banner.reload')}
        </button>
      </div>
    </div>
  )
}
